// src/pages/HealthViewPage.js
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Alert, Badge, Button, Card, Col, Row, Spinner } from "react-bootstrap";
import api from "../api";
import ClientContactLine from "../components/ClientContactLine";

const INSURANCE_COMPANY_SUGGESTIONS = [
  "Blue Cross Blue Shield",
  "Medicaid",
  "Medicare",
  "Priority Health",
  "Molina",
  "Aetna",
  "Cigna",
  "UnitedHealthcare",
  "Tricare",
  "Other",
];

// Must match your edit page
const FAMILY_MEMBERS_ENDPOINT = (id) => `/clients/${id}/family-members`;

function safeParseJson(s) {
  if (!s) return null;
  try {
    return JSON.parse(s);
  } catch {
    return null;
  }
}

function memberLabel(m) {
  const fn = m?.first_name || "";
  const ln = m?.last_name || "";
  const name = `${fn} ${ln}`.trim();
  // family_members store a single full_name column
  return name || m?.full_name || m?.name || `Member ${m?.id}`;
}

function ynOrDashFromBool(v) {
  if (v === true) return "Yes";
  if (v === false) return "No";
  return "—";
}

function displayOrDash(v) {
  const s = v == null ? "" : String(v).trim();
  return s ? s : "—";
}

// insurance_company is stored as a comma-separated string; treat it as a list.
function splitInsurers(s) {
  return (s || "").split(",").map((x) => x.trim()).filter(Boolean);
}

function resolveCoveredLabels(coveredIds, familyMembers, client) {
  // "Who is covered" is built from real, named people: the client plus every
  // family member (spouse, child, boyfriend, etc. — each shown by name). The
  // legacy synthetic "spouse" id resolves only when a spouse_name is on file;
  // otherwise it (and any deleted member) is dropped rather than shown as a
  // bare relationship title.
  const clientName = `${client?.first_name || ""} ${client?.last_name || ""}`.trim();
  const spouseName = (client?.spouse_name || "").trim();

  const map = new Map();
  map.set("client", clientName || "Client");
  if (spouseName) map.set("spouse", spouseName);
  (familyMembers || []).forEach((m) => map.set(String(m.id), memberLabel(m)));

  return (coveredIds || []).map((cid) => map.get(String(cid))).filter(Boolean);
}

export default function HealthViewPage() {
  const { id } = useParams(); // client id
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [familyMembers, setFamilyMembers] = useState([]);
  const [client, setClient] = useState(null);

  // Loaded medical record (single)
  const [medical, setMedical] = useState(null);

  // Derived insurance coverage data
  const insuranceParsed = useMemo(() => {
    const parsed = safeParseJson(medical?.insurance_coverage_details);
    const covered_member_ids = Array.isArray(parsed?.covered_member_ids) ? parsed.covered_member_ids : [];
    const notes = typeof parsed?.notes === "string" ? parsed.notes : "";
    return {
      covered_member_ids: covered_member_ids.map(String),
      notes,
    };
  }, [medical?.insurance_coverage_details]);

  const coveredLabels = useMemo(() => {
    return resolveCoveredLabels(insuranceParsed.covered_member_ids, familyMembers, client);
  }, [insuranceParsed.covered_member_ids, familyMembers, client]);

  useEffect(() => {
    let isMounted = true;

    async function load() {
      setLoading(true);
      setError("");

      try {
        // Family members (optional)
        try {
          const fmRes = await api.get(FAMILY_MEMBERS_ENDPOINT(id));
          const d = fmRes?.data || {};
          // The endpoint returns a bare array; tolerate an {items:[...]} shape too.
          if (isMounted) setFamilyMembers(Array.isArray(d) ? d : Array.isArray(d.items) ? d.items : []);
        } catch {
          if (isMounted) setFamilyMembers([]);
        }

        // Client (for insured names) — optional
        try {
          const cRes = await api.get(`/clients/${id}`);
          if (isMounted) setClient(cRes?.data || null);
        } catch {
          if (isMounted) setClient(null);
        }

        // Health/medical info (optional)
        const res = await api.get(`/clients/${id}/medical`);
        const item = res?.data?.item || null;

        if (!isMounted) return;
        setMedical(item);
      } catch (e) {
        // Match your pattern: don't block UI if endpoint doesn't exist yet
        // If you want a visible error, uncomment:
        // setError(e?.response?.data?.error || e?.message || "Failed to load health info.");
        if (!isMounted) return;
        setMedical(null);
      } finally {
        if (isMounted) setLoading(false);
      }
    }

    load();
    return () => {
      isMounted = false;
    };
  }, [id]);

  if (loading) {
    return (
      <div className="p-3">
        <Spinner animation="border" size="sm" className="me-2" />
        Loading health...
      </div>
    );
  }

  // Convenience bools
  const hasHouseholdIssues = medical?.has_household_medical_issues === true;
  const takesMeds = medical?.takes_medication_regularly === true;
  const hasInsurance = medical?.has_medical_insurance === true;
  const receivedCounseling = medical?.received_counseling === true;
  const hadTrauma = medical?.recent_trauma === true;
  const alcoholDrugProblem = medical?.alcohol_or_drug_problem === true;

  return (
    <div className="p-3">
      <Row className="mb-3">
        <Col>
          <h3 className="mb-0">Health</h3>
          <ClientContactLine client={client} />
          <div className="text-muted">Client ID: {id}</div>
        </Col>

        <Col className="d-flex justify-content-end align-items-start gap-2">
          <Button variant="outline-secondary" type="button" onClick={() => navigate("/clients")}>
            Back to Clients
          </Button>
          <Button variant="primary" type="button" onClick={() => navigate(`/clients/${id}/health`)}>
            Edit Health
          </Button>
        </Col>
      </Row>

      {error ? (
        <Alert variant="danger" className="mb-3">
          {error}
        </Alert>
      ) : null}

      {/* If there is no medical record returned, show an informational hint but still render sections */}
      {!medical ? (
        <Alert variant="info" className="mb-3">
          No health information recorded yet.
        </Alert>
      ) : null}

      {/* 1) Household medical/hearing/vision issues */}
      <Card className="mb-3">
        <Card.Header>Household medical, hearing, or vision issues</Card.Header>
        <Card.Body>
          <div className="mb-2">
            <div className="text-muted" style={{ fontSize: 13 }}>
              Does anyone in your household have medical, hearing, or vision issues?
            </div>
            <div className="d-flex align-items-center gap-2">
              <Badge bg={medical?.has_household_medical_issues === true ? "success" : "secondary"}>
                {ynOrDashFromBool(medical?.has_household_medical_issues)}
              </Badge>
            </div>
          </div>

          {hasHouseholdIssues ? (
            <div>
              <div className="text-muted" style={{ fontSize: 13 }}>
                Explain
              </div>
              <div style={{ whiteSpace: "pre-wrap" }}>{displayOrDash(medical?.household_medical_issues)}</div>
            </div>
          ) : null}
        </Card.Body>
      </Card>

      {/* 2) Takes medication regularly */}
      <Card className="mb-3">
        <Card.Header>Medication</Card.Header>
        <Card.Body>
          <div className="mb-2">
            <div className="text-muted" style={{ fontSize: 13 }}>
              Does anyone in your household take medication regularly?
            </div>
            <div className="d-flex align-items-center gap-2">
              <Badge bg={medical?.takes_medication_regularly === true ? "success" : "secondary"}>
                {ynOrDashFromBool(medical?.takes_medication_regularly)}
              </Badge>
            </div>
          </div>

          {takesMeds ? (
            <div>
              <div className="text-muted" style={{ fontSize: 13 }}>
                Notes
              </div>
              <div style={{ whiteSpace: "pre-wrap" }}>{displayOrDash(medical?.medication_notes)}</div>
            </div>
          ) : null}
        </Card.Body>
      </Card>

      {/* 3) Medical insurance */}
      <Card className="mb-3">
        <Card.Header>Medical Insurance</Card.Header>
        <Card.Body>
          <div className="mb-2">
            <div className="text-muted" style={{ fontSize: 13 }}>
              Do you have medical insurance?
            </div>
            <div className="d-flex align-items-center gap-2">
              <Badge bg={medical?.has_medical_insurance === true ? "success" : "secondary"}>
                {ynOrDashFromBool(medical?.has_medical_insurance)}
              </Badge>
            </div>
          </div>

          {hasInsurance ? (
            <Row className="g-3">
              <Col md={6}>
                <div className="text-muted" style={{ fontSize: 13 }}>
                  Insurance Company
                </div>
                <div className="d-flex flex-wrap gap-2 mt-1">
                  {splitInsurers(medical?.insurance_company).length ? (
                    splitInsurers(medical?.insurance_company).map((c, i) => (
                      <Badge key={`${c}-${i}`} bg="secondary">{c}</Badge>
                    ))
                  ) : (
                    <span className="text-muted">—</span>
                  )}
                </div>
              </Col>

              <Col md={6}>
                <div className="text-muted" style={{ fontSize: 13 }}>
                  Which household members are covered?
                </div>
                <div className="d-flex flex-wrap gap-2 mt-1">
                  {coveredLabels.length ? (
                    coveredLabels.map((lbl, i) => (
                      <Badge key={`${lbl}-${i}`} bg="info">
                        {lbl}
                      </Badge>
                    ))
                  ) : (
                    <span className="text-muted">—</span>
                  )}
                </div>
              </Col>

              <Col md={12}>
                <div className="text-muted" style={{ fontSize: 13 }}>
                  Coverage details (optional)
                </div>
                <div style={{ whiteSpace: "pre-wrap" }}>
                  {displayOrDash(insuranceParsed.notes)}
                </div>
              </Col>
            </Row>
          ) : null}
        </Card.Body>
      </Card>

      {/* 4) Counseling */}
      <Card className="mb-3">
        <Card.Header>Counseling</Card.Header>
        <Card.Body>
          <div className="mb-2">
            <div className="text-muted" style={{ fontSize: 13 }}>
              Have you ever received any counseling?
            </div>
            <div className="d-flex align-items-center gap-2">
              <Badge bg={medical?.received_counseling === true ? "success" : "secondary"}>
                {ynOrDashFromBool(medical?.received_counseling)}
              </Badge>
            </div>
          </div>

          {receivedCounseling ? (
            <Row className="g-3">
              <Col md={12}>
                <div className="text-muted" style={{ fontSize: 13 }}>
                  Explain
                </div>
                <div style={{ whiteSpace: "pre-wrap" }}>
                  {displayOrDash(medical?.counseling_details)}
                </div>
              </Col>

              <Col md={6}>
                <div className="text-muted" style={{ fontSize: 13 }}>
                  Counselor Name
                </div>
                <div>{displayOrDash(medical?.counselor_name)}</div>
              </Col>

              <Col md={6}>
                <div className="text-muted" style={{ fontSize: 13 }}>
                  Counselor Phone
                </div>
                <div>{displayOrDash(medical?.counselor_phone)}</div>
              </Col>
            </Row>
          ) : null}
        </Card.Body>
      </Card>

      {/* 5) Recent trauma */}
      <Card className="mb-3">
        <Card.Header>Recent Losses / Trauma</Card.Header>
        <Card.Body>
          <div className="mb-2">
            <div className="text-muted" style={{ fontSize: 13 }}>
              Have you experienced any recent losses or serious traumas in the past six months (death, divorce,
              accident, loss of job)?
            </div>
            <div className="d-flex align-items-center gap-2">
              <Badge bg={medical?.recent_trauma === true ? "success" : "secondary"}>
                {ynOrDashFromBool(medical?.recent_trauma)}
              </Badge>
            </div>
          </div>

          {hadTrauma ? (
            <div>
              <div className="text-muted" style={{ fontSize: 13 }}>
                Explain
              </div>
              <div style={{ whiteSpace: "pre-wrap" }}>{displayOrDash(medical?.trauma_details)}</div>
            </div>
          ) : null}
        </Card.Body>
      </Card>

      {/* 6) Alcohol/drug problem */}
      <Card className="mb-3">
        <Card.Header>Alcohol / Drug Use</Card.Header>
        <Card.Body>
          <div className="mb-2">
            <div className="text-muted" style={{ fontSize: 13 }}>
              Do you or have you in the past had a problem with alcohol or drug use?
            </div>
            <div className="d-flex align-items-center gap-2">
              <Badge bg={medical?.alcohol_or_drug_problem === true ? "success" : "secondary"}>
                {ynOrDashFromBool(medical?.alcohol_or_drug_problem)}
              </Badge>
            </div>
          </div>

          {alcoholDrugProblem ? (
            <div>
              <div className="text-muted" style={{ fontSize: 13 }}>
                Explain
              </div>
              <div style={{ whiteSpace: "pre-wrap" }}>{displayOrDash(medical?.alcohol_drug_details)}</div>
            </div>
          ) : null}
        </Card.Body>
      </Card>
    </div>
  );
}
